import { Signal, signal, WritableSignal } from "@angular/core";
import { AddressFieldData, UtilityService } from "@eforall/common";
import { AngularFormDialogData } from "@eforall/dashboard";
import { ContactMethodId, ExploredPerson, PersonWebLink, PhonetypeId, WeblinktypeId } from "@interfaces";
import { PersonExplorerService } from "../person-explorer.service";

interface NameForm {
	firstName: string,
	middleInit: string | undefined,
	lastName: string,
}

interface PhoneForm {
	phone: string,
	phoneTypeId: PhonetypeId | undefined,
}

interface WebLinkForm {
	url: string,
	webLinkTypeId: WeblinktypeId,
}

// Helper function to create dialog data for editing a person's name
export function getNameEditorDialogData(person: ExploredPerson, personExplorerService: PersonExplorerService) {
	const dialogData: AngularFormDialogData<NameForm> = {
		header: 'Edit Name',
		fields: [
			{
				kind: 'text',
				formFieldName: 'firstName',
				label: 'First Name',
				initialValue: signal(person.general.firstName),
				validations: {
					isRequired: signal(true),
					maxLength: 20
				}
			},
			{
				kind: 'text',
				formFieldName: 'middleInit',
				label: 'Middle Initial',
				initialValue: signal(person.general.middleInit),
				validations: {
					isRequired: signal(false),
					maxLength: 1
				},
			},
			{
				kind: 'text',
				formFieldName: 'lastName',
				label: 'Last Name',
				initialValue: signal(person.general.lastName),
				validations: {
					isRequired: signal(true),
					maxLength: 20
				},
			},
		],
		onSave: async ({ firstName, middleInit, lastName }: NameForm) => {
			await personExplorerService.updatePersonDetails({ firstName, middleInit, lastName });
		}
	};
	return dialogData;
}


// Helper function to create dialog data for editing a person's address
export function getAddressEditorDialogData(person: ExploredPerson, util: UtilityService, personExplorerService: PersonExplorerService) {
	const dialogData: AngularFormDialogData<{ address: AddressFieldData }> = {
		header: 'Edit Address',
		fields: [
			{
				kind: 'address',
				formFieldName: 'address',
				label: 'Address',
				initialValue: signal({
					placeAddress: person.location?.address ?? '',
					noSelection: true,
					streetAddress: undefined,
					zipId: undefined,
					googlePlaceKey: person.location?.googlePlaceKey,
					latitude: undefined,
					longitude: undefined,
					stateCode: undefined,
				}),
				funcGetCountyFipsId: personExplorerService.explorer.func.person.getCountyFipsId,
				validations: {
					isRequired: signal(true),
				}
			},
		],
		onSave: async (values: { address: AddressFieldData }) => {
			if (util.values.areSame(person.location?.googlePlaceKey, values.address.googlePlaceKey)) {
				console.log('No changes made to address, skipping update.');
				return;
			}
			await personExplorerService.updatePersonAddress(values.address);
		}
	}
	return dialogData;
}


// Helper function to create dialog data for editing a person's preferred contact method
export function getContactMethodEditorDialogData(person: ExploredPerson, personExplorerService: PersonExplorerService, contactMethodOptions: Signal<{ value: ContactMethodId, text: string }[]>) {
	const dialogData: AngularFormDialogData<{ contactMethodId: ContactMethodId }> = {
		header: 'Edit Contact Method',
		fields: [
			{
				kind: 'select',
				formFieldName: 'contactMethodId',
				label: signal('Preferred Contact Method'),
				initialValue: signal(person.general.contactMethodId),
				options: contactMethodOptions,
				validations: {
					isRequired: signal(true),
				},
			},
		],
		onSave: async (values: { contactMethodId: ContactMethodId }) => {
			await personExplorerService.updatePersonDetails(values);
		}
	}
	return dialogData;
}

// Helper function to create dialog data for editing a person's phone details
export function getPhoneEditorDialogData(person: ExploredPerson, personExplorerService: PersonExplorerService, phoneTypeOptions: Signal<{ value: PhonetypeId, text: string, }[]>) {
	const dialogData: AngularFormDialogData<PhoneForm> = {
		header: 'Edit Phone Number',
		fields: [
			{
				kind: 'text',
				formFieldName: 'phone',
				label: 'Phone',
				initialValue: signal(person.general.phone),
				validations: {
					isRequired: signal(true),
					isPhone: true
				}
			},
			{
				kind: 'select',
				formFieldName: 'phoneTypeId',
				label: 'Phone Type',
				initialValue: signal(person.general.phoneTypeId),
				options: phoneTypeOptions,
				validations: {
					isRequired: signal(false)
				},
			},
		],
		onSave: async ({ phone, phoneTypeId }: PhoneForm) => {
			await personExplorerService.updatePersonDetails({ phone, phoneTypeId });
		}
	}
	return dialogData;
}


// Helper function to create dialog data for editing a person's "Do Not Contact" note
export function getDoNotContactEditorDialogData(person: ExploredPerson, personExplorerService: PersonExplorerService) {
	const dialogData: AngularFormDialogData<{ doNotContact: string }> = {
		header: 'Edit Do Not Contact Note',
		fields: [
			{
				kind: 'text',
				formFieldName: 'doNotContact',
				label: 'Do Not Contact',
				initialValue: signal(person.general.doNotContactNote),
				isMultiLine: true,
			},
		],
		onSave: async (values: { doNotContact: string }) => {
			console.log('Saved Form Data:', values);
			await personExplorerService.updatePersonDetails({ doNotContactNote: values.doNotContact });
		}
	}
	return dialogData;
}


// Helper function to create dialog data for adding a new email address for a person
export function getEmailEditorDialogData(personExplorerService: PersonExplorerService, emailErrorMsg: WritableSignal<string | undefined>) {
	const dialogData: AngularFormDialogData<{ email: string }> = {
		header: 'Add Email',
		fields: [
			{
				kind: 'text',
				formFieldName: 'email',
				label: 'Email',
				initialValue: signal(undefined),
				validations: {
					isRequired: signal(true),
					isEmail: true
				}
			},
		],
		onSave: async (values: { email: string }) => {
			const result = await personExplorerService.addEmail(values.email);
			if (result.errorMessage) {
				emailErrorMsg.set(result.errorMessage);
			}
		}
	}
	return dialogData;
}


// Helper function to create dialog data for adding or editing a person's web link
export function getWebLinkEditorDialogData(personExplorerService: PersonExplorerService, webLinkOptions: Signal<{ value: WeblinktypeId, text: string }[]>, webLink?: PersonWebLink) {

	const dialogData: AngularFormDialogData<WebLinkForm> = {
		header: webLink ? 'Edit Web Link' : 'Add Web Link',
		fields: [
			{
				kind: 'text',
				formFieldName: 'url',
				label: 'URL',
				initialValue: signal(webLink?.url),
				validations: {
					isRequired: signal(true),
					isURL: true
				}
			},
			{
				kind: 'select',
				formFieldName: 'webLinkTypeId',
				label: 'WebLink Type',
				initialValue: signal(webLink?.webLinkTypeId),
				options: webLinkOptions,
				validations: {
					isRequired: signal(true)
				},
			},
		],
		onSave: async (values: WebLinkForm) => {
			if (webLink) {
				await personExplorerService.updateWebLink(webLink.webLinkId, values.webLinkTypeId, values.url, false);
			} else {
				await personExplorerService.addWeblink(values.webLinkTypeId, values.url);
			}
		}
	}

	return dialogData;
}